import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "column q-gutter-md" }
const _hoisted_2 = { class: "row q-gutter-md items-end" }
const _hoisted_3 = { class: "col-xs-12 col-md" }
const _hoisted_4 = { class: "col-xs-6 col-md-2" }
const _hoisted_5 = { class: "col-xs-6 col-md-2" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "row justify-end q-gutter-md s-modal__form-footer" }

import { toRussianLocaleDateTimeFromIso } from '@packages/toolbox/dist/utils/datetime';
import { computed, ref, watch } from 'vue';

import ExtensionIcon from 'SharedComponents/Attachments/ExtensionIcon.vue';
import { AttachmentsApi } from 'SharedDir/backend/api';
import { makeRequest } from 'SharedDir/common/composables/useRequests';
import { FileFull } from 'SharedDir/declarations/files';
import { BasePaginated } from 'SharedDir/declarations/pagination';
import { QuasarTableColumnList } from 'SharedDir/declarations/shared';
import { UserBrief } from 'SharedDir/declarations/user';
import { DateTimeColumn } from 'SharedDir/utils/quasar';

interface Props {
  modelValue: boolean;
  multiple?: boolean;
  maxFiles?: number;
  /**
   * CSV из QuasarFileUploader.validtyAcceptExtensions вида `.pdf,.docx,*`
   * либо массив расширений (`['pdf','docx']`). Если присутствует `*` —
   * фильтр игнорируется (все расширения допустимы).
   */
  extensions?: string | string[];
}


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'ExistingFilePicker' },
  __name: 'ExistingFilePicker',
  props: {
    modelValue: { type: Boolean },
    multiple: { type: Boolean, default: false },
    maxFiles: { default: 10 },
    extensions: { default: undefined }
  },
  emits: ["update:modelValue", "pick"],
  setup(__props: any, { emit: __emit }) {



const props = __props;

const emit = __emit;

const title = computed(() => (props.multiple ? 'Выбор файлов' : 'Выбор файла'));

const searchName = ref<string | null>(null);
const fromDate = ref<string | null>(null);
const toDate = ref<string | null>(null);
const selected = ref<FileFull[]>([]);
const rows = ref<FileFull[]>([]);
const loading = ref(false);
let fetchSequence = 0;

const pagination = ref<BasePaginated>({
  page: 1,
  rowsPerPage: 10,
  sortBy: 'created_at',
  descending: true,
  rowsNumber: 0,
});

const columns: QuasarTableColumnList<FileFull> = [
  { name: 'name', field: 'name', label: 'Имя', align: 'left', sortable: true },
  { name: 'size', field: 'size', label: 'Размер', align: 'right', sortable: true },
  { name: 'createdUser', field: 'createdUser', label: 'Автор', align: 'left' },
  DateTimeColumn({
    name: 'createdAt',
    field: 'createdAt',
    label: 'Загружен',
    align: 'center',
    sortable: true,
  }),
];

/**
 * Превращает входной CSV/массив расширений в нормализованный список
 * для отправки на бэк (`extension` query). `'*'` означает, что фильтра
 * по расширению не нужно.
 */
const normalizedExtensions = computed<string[] | null>(() => {
  if (!props.extensions) {
    return null;
  }
  const raw = Array.isArray(props.extensions) ? props.extensions : props.extensions.split(',');
  const cleaned = raw
    .map((item) => item.trim().toLowerCase())
    .map((item) => (item.startsWith('.') ? item.slice(1) : item))
    .filter((item) => item.length > 0);
  if (cleaned.length === 0 || cleaned.includes('*')) {
    return null;
  }
  return cleaned;
});

async function fetchFiles() {
  const sequence = ++fetchSequence;
  loading.value = true;
  try {
    const response = await makeRequest(() =>
      AttachmentsApi.getPaginated({
        page: pagination.value.page,
        rowsPerPage: pagination.value.rowsPerPage,
        sortBy: pagination.value.sortBy,
        descending: pagination.value.descending,
        name: searchName.value || null,
        extension: normalizedExtensions.value,
        fromDate: fromDate.value,
        toDate: toDate.value,
      })
    );
    if (sequence !== fetchSequence || !response) return;
    rows.value = response.data;
    pagination.value = {
      ...pagination.value,
      page: response.page ?? pagination.value.page,
      rowsPerPage: response.rowsPerPage ?? pagination.value.rowsPerPage,
      rowsNumber: response.rowsNumber ?? rows.value.length,
    };
  } finally {
    if (sequence === fetchSequence) {
      loading.value = false;
    }
  }
}

function refetch() {
  pagination.value = { ...pagination.value, page: 1 };
  fetchFiles();
}

function onRequest(payload: { pagination: BasePaginated }) {
  pagination.value = payload.pagination;
  fetchFiles();
}

function clearFilters() {
  searchName.value = null;
  fromDate.value = null;
  toDate.value = null;
  refetch();
}

function onSelectedUpdate(value: readonly FileFull[]) {
  if (!props.multiple) {
    selected.value = [...value];
    return;
  }
  selected.value = [...value].slice(0, props.maxFiles);
}

function pickRow(row: FileFull) {
  if (props.multiple) {
    const exists = selected.value.find((item) => item.id === row.id);
    if (exists) {
      selected.value = selected.value.filter((item) => item.id !== row.id);
    } else if (selected.value.length < props.maxFiles) {
      selected.value = [...selected.value, row];
    }
    return;
  }
  selected.value = [row];
  confirm();
}

function confirm() {
  if (selected.value.length === 0) return;
  const result = props.multiple
    ? selected.value.slice(0, props.maxFiles)
    : selected.value.slice(0, 1);
  emit('pick', result);
  emit('update:modelValue', false);
}

function cancel() {
  emit('update:modelValue', false);
}

function onDialogVisibilityChange(value: boolean) {
  emit('update:modelValue', value);
  if (!value) {
    selected.value = [];
    searchName.value = null;
    fromDate.value = null;
    toDate.value = null;
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      selected.value = [];
      pagination.value = {
        ...pagination.value,
        page: 1,
      };
      fetchFiles();
    }
  }
);

function buildFullName(file: FileFull): string {
  if (!file.extension) return file.name;
  return `${file.name}.${file.extension}`;
}

function formatFileSize(size: number | null): string {
  if (size === null || size === undefined) return '—';
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  let current = size;
  let unitIndex = 0;
  while (current >= 1024 && unitIndex < units.length - 1) {
    current /= 1024;
    unitIndex += 1;
  }
  const formatted = Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(current);
  return `${formatted} ${units[unitIndex]}`;
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  return toRussianLocaleDateTimeFromIso(value);
}

function formatUser(user: UserBrief | null | undefined): string {
  if (!user) return '—';
  const middle = user.middleName ? ` ${user.middleName}` : '';
  return `${user.lastName} ${user.firstName}${middle}`.trim();
}

return (_ctx: any,_cache: any) => {
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_s_input = _resolveComponent("s-input")!
  const _component_s_input_date = _resolveComponent("s-input-date")!
  const _component_s_btn_clear = _resolveComponent("s-btn-clear")!
  const _component_q_td = _resolveComponent("q-td")!
  const _component_s_table = _resolveComponent("s-table")!
  const _component_s_btn = _resolveComponent("s-btn")!
  const _component_s_modal = _resolveComponent("s-modal")!

  return (_ctx.modelValue)
    ? (_openBlock(), _createBlock(_component_s_modal, {
        key: 0,
        "model-value": _ctx.modelValue,
        title: title.value,
        size: "large",
        "onUpdate:modelValue": onDialogVisibilityChange,
        onClose: cancel
      }, {
        footer: _withCtx(() => [
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_component_s_btn, {
              type: "secondary",
              label: "Отменить",
              onClick: cancel
            }),
            _createVNode(_component_s_btn, {
              label: "Выбрать",
              disable: selected.value.length === 0,
              onClick: confirm
            }, null, 8, ["disable"])
          ])
        ]),
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", _hoisted_3, [
                _createVNode(_component_s_input, {
                  modelValue: searchName.value,
                  "onUpdate:modelValue": [
                    _cache[0] || (_cache[0] = ($event: any) => ((searchName).value = $event)),
                    refetch
                  ],
                  dense: "",
                  outlined: "",
                  clearable: "",
                  placeholder: "Поиск по имени файла",
                  debounce: "500"
                }, {
                  append: _withCtx(() => [
                    _createVNode(_component_q_icon, { name: "app:search" })
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              _createElementVNode("div", _hoisted_4, [
                _createVNode(_component_s_input_date, {
                  modelValue: fromDate.value,
                  "onUpdate:modelValue": [
                    _cache[1] || (_cache[1] = ($event: any) => ((fromDate).value = $event)),
                    refetch
                  ],
                  dense: "",
                  outlined: "",
                  clearable: "",
                  label: "С"
                }, null, 8, ["modelValue"])
              ]),
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_component_s_input_date, {
                  modelValue: toDate.value,
                  "onUpdate:modelValue": [
                    _cache[2] || (_cache[2] = ($event: any) => ((toDate).value = $event)),
                    refetch
                  ],
                  dense: "",
                  outlined: "",
                  clearable: "",
                  label: "По"
                }, null, 8, ["modelValue"])
              ]),
              _createVNode(_component_s_btn_clear, { onClick: clearFilters })
            ]),
            _createVNode(_component_s_table, {
              selected: selected.value,
              "onUpdate:selected": [
                _cache[3] || (_cache[3] = ($event: any) => ((selected).value = $event)),
                onSelectedUpdate
              ],
              pagination: pagination.value,
              "onUpdate:pagination": _cache[4] || (_cache[4] = ($event: any) => ((pagination).value = $event)),
              "row-key": "id",
              selection: _ctx.multiple ? 'multiple' : 'single',
              rows: rows.value,
              columns: columns,
              loading: loading.value,
              onRequest: onRequest
            }, {
              "body-cell-name": _withCtx((props) => [
                _createVNode(_component_q_td, { props: props }, {
                  default: _withCtx(() => [
                    _createElementVNode("span", {
                      class: "row no-wrap items-center q-gutter-xs cursor-pointer",
                      onClick: ($event: any) => (pickRow(props.row))
                    }, [
                      _createVNode(ExtensionIcon, {
                        extension: props.row.extension
                      }, null, 8, ["extension"]),
                      _createElementVNode("span", null, _toDisplayString(buildFullName(props.row)), 1)
                    ], 8, _hoisted_6)
                  ]),
                  _: 2
                }, 1032, ["props"])
              ]),
              "body-cell-size": _withCtx((props) => [
                _createVNode(_component_q_td, { props: props }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(formatFileSize(props.row.size)), 1)
                  ]),
                  _: 2
                }, 1032, ["props"])
              ]),
              "body-cell-createdAt": _withCtx((props) => [
                _createVNode(_component_q_td, { props: props }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(formatDateTime(props.row.createdAt)), 1)
                  ]),
                  _: 2
                }, 1032, ["props"])
              ]),
              "body-cell-createdUser": _withCtx((props) => [
                _createVNode(_component_q_td, { props: props }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(formatUser(props.row.createdUser)), 1)
                  ]),
                  _: 2
                }, 1032, ["props"])
              ]),
              _: 1
            }, 8, ["selected", "pagination", "selection", "rows", "loading"])
          ])
        ]),
        _: 1
      }, 8, ["model-value", "title"]))
    : _createCommentVNode("", true)
}
}

})